import React, { useEffect, useRef, useState } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import {
  getAuthenticatedWebSocketUrl,
  setSessionVariable,
} from "../Utils/CoTraderUtils";
import CoTraderForwardChart from "../Components/CoTraderForwardChartTileComponent";
import BestxSummaryTileComponent from "../Components/BestX/BestXSummaryTileComponent";
import PriceChartTileComponent from "../Components/CoTraderPriceChartTileComponent";
import { TradeAnalysisTradeHeader } from "../Components/TradeAnalysis/TradeAnalysisTradeHeader";
import TradeAnalysisPopOverMenu from "../Components/TradeAnalysis/TradeAnalysisPopOverMenu";
import TradeAnalysisConversationTileComponent from "../Components/TradeAnalysis/TradeAnalysisConversationTileComponent";
import TradeAnalysisLandingPage from "../Components/TradeAnalysis/TradeAnalysisLandingPage";
import { sortForwardTenors } from "../Utils/CoTraderUtils";
import { parseUtcTimestampToMs } from "../Utils/timezone";

interface Context {
  id?: {
    [key: string]: string;
  };
  name?: string;
  type: string;
  [s: string]: any;
}
interface ContextProp {
  context: Context;
  currencyList: string[];
}

type TradeAnalysisLandingField =
  | "currencyPair"
  | "tradeType"
  | "executionTimestamp"
  | "executionSpotRate"
  | "executionForwardPoints"
  | "expirationTimestamp";

type TradeAnalysisLandingPrefill = {
  currencyPair: string;
  tradeType: "BUY" | "SELL" | "";
  executionTimestamp: string;
  executionSpotRate: number | null;
  executionForwardPoints: number | null;
  expirationTimestamp: string;
};

const TRADE_ANALYSIS_PRICE_WINDOW_MS = 60 * 60 * 1000;

interface BestXSelectedTradeContext extends Context {
  type: "bestx.selectedTrade";
  ccyPair?: string;
  completionTime?: string;
  product?: string;
  valueDate?: string;
  executedRate?: number | string | null;
  executionRate?: number | string | null;
  executionSpotRate?: number | string | null;
  spotRate?: number | string | null;
  rate?: number | string | null;
  executionForwardPoints?: number | string | null;
  forwardPoints?: number | string | null;
  tradeType?: string;
  side?: string;
  direction?: string;
}

const isBestXSelectedTradeContext = (
  context: Context | null | undefined
): context is BestXSelectedTradeContext => context?.type === "bestx.selectedTrade";

const parseTradeNumber = (value: unknown) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getFirstTradeNumber = (
  context: BestXSelectedTradeContext,
  keys: string[]
) => {
  for (const key of keys) {
    const parsed = parseTradeNumber(context[key]);
    if (parsed != null) return parsed;
  }
  return null;
};

const getTradeTypeFromContext = (context: BestXSelectedTradeContext) => {
  const rawValue = context.tradeType ?? context.side ?? context.direction ?? "";
  const normalized = String(rawValue).trim().toUpperCase();
  return normalized === "BUY" || normalized === "SELL" ? normalized : "";
};

const buildTradeAnalysisLandingPrefill = (
  context: BestXSelectedTradeContext
): TradeAnalysisLandingPrefill => ({
  currencyPair: context.ccyPair ?? "",
  tradeType: getTradeTypeFromContext(context),
  executionTimestamp: context.completionTime ?? "",
  executionSpotRate: getFirstTradeNumber(context, [
    "executedRate",
    "executionRate",
    "executionSpotRate",
    "spotRate",
    "rate",
  ]),
  executionForwardPoints: getFirstTradeNumber(context, [
    "executionForwardPoints",
    "forwardPoints",
  ]),
  expirationTimestamp: context.valueDate ?? "",
});

const getTradeAnalysisMissingFields = (trade: TradeAnalysisLandingPrefill) => {
  const missingFields: TradeAnalysisLandingField[] = [];
  if (!trade.currencyPair) missingFields.push("currencyPair");
  if (!trade.tradeType) missingFields.push("tradeType");
  if (!trade.executionTimestamp) missingFields.push("executionTimestamp");
  return missingFields;
};

const getTradeAnalysisSpotWindow = (executionTimestamp: string) => {
  // Execution timestamps are UTC without a zone suffix.
  const executionMs = parseUtcTimestampToMs(executionTimestamp);
  if (!Number.isFinite(executionMs)) {
    return {
      startDate: undefined,
      endDate: undefined,
    };
  }

  return {
    startDate: new Date(
      executionMs - TRADE_ANALYSIS_PRICE_WINDOW_MS
    ).toISOString(),
    endDate: new Date(
      executionMs + TRADE_ANALYSIS_PRICE_WINDOW_MS
    ).toISOString(),
  };
};

const parseSummaryPayload = (message: any) => {
  if (message == null) return null;

  if (typeof message === "string") {
    try {
      return JSON.parse(message);
    } catch {
      return null;
    }
  }

  return message;
};

const TradeAnalysisTile: React.FunctionComponent<ContextProp> = ({
  context,
  currencyList,
}) => {
  const theme = useTheme();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<"summary" | "chat">("summary");
  const [activeChartTab, setActiveChartTab] = useState<"prices" | "forward">(
    "prices"
  );
  const [useCompactCharts, setUseCompactCharts] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 8 : false
  );
  const [currencyPair, setCurrencyPair] = useState("");
  const [tradeType, setTradeType] = useState<"BUY" | "SELL">("BUY");
  const [useContext, setUseContext] = useState(true);
  const [, setCurrentPrice] = useState(0);
  const [executionTimestamp, setExecutionTimestamp] = useState("");
  const [executionSpotRate, setExecutionSpotRate] = useState<number | null>(
    null
  );
  const [executionForwardPoints, setExecutionForwardPoints] = useState<
    number | null
  >(null);
  const [expiration_timestamp, setExpiration_timestamp] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<string>("");
  const summaryCacheRef = useRef("");
  const summaryVersionRef = useRef(0);
  const [userLanguage, setUserLanguage] = useState(
    localStorage.getItem("user_language") || "English"
  );
  const [tenorList, setTenorList] = useState([]);
  const [tenors, setTenors]: any = useState([]);
  const [landingPrefill, setLandingPrefill] =
    useState<TradeAnalysisLandingPrefill | null>(null);
  const [landingNotice, setLandingNotice] = useState("");
  const [landingRequiredFields, setLandingRequiredFields] = useState<
    TradeAnalysisLandingField[]
  >([]);

  const handleTradeSubmit = (trade: {
    currencyPair: string;
    tradeType: "BUY" | "SELL";
    executionTimestamp: string;
    executionSpotRate: number | null;
    executionForwardPoints: number | null;
    expirationTimestamp: string;
  }) => {
    setCurrencyPair(trade.currencyPair);
    setTradeType(trade.tradeType);
    setExecutionTimestamp(trade.executionTimestamp);
    setExecutionSpotRate(trade.executionSpotRate);
    setExecutionForwardPoints(trade.executionForwardPoints);
    setExpiration_timestamp(trade.expirationTimestamp);
    setLandingNotice("");
    setLandingRequiredFields([]);
    setUseContext(false);
    setHasSubmitted(true);
  };

  const handleSetTenorList = React.useCallback(
    (nextTenorList: string[] = []) => {
      setTenorList(sortForwardTenors(nextTenorList) as any);
    },
    []
  );
  const handleResetToLanding = () => {
    summaryCacheRef.current = "";
    summaryVersionRef.current = 0;
    setHasSubmitted(false);
    setUseContext(true);
    setCurrencyPair("");
    setTradeType("BUY");
    setCurrentPrice(0);
    setExecutionTimestamp("");
    setExecutionSpotRate(null);
    setExecutionForwardPoints(null);
    setExpiration_timestamp("");
    setSummary("");
    setLoading(false);
    setTenorList([]);
    setTenors([]);
    setLandingPrefill(null);
    setLandingNotice("");
    setLandingRequiredFields([]);
    setActiveTab("summary");
    setActiveChartTab("prices");

    localStorage.removeItem("trade_analysis_exec_ts");
    localStorage.removeItem("trade_analysis_exec_rate");
    localStorage.removeItem("trade_analysis_exec_fwd");
    localStorage.removeItem("trade_analysis_expiry");
  };

  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
      width: "100%",
      padding: "10px",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
    },
    chartSection: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      flexShrink: 0,
      "@media (max-height: 480px)": {
        display: "none",
      },
    },
    chartRow: {
      display: "flex",
      gap: "12px",
      width: "100%",
      minHeight: 0,
    },
    chartCard: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      borderRadius: "10px",
      padding: "8px",
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(255,255,255,0.04)",
      overflow: "hidden",
    },
    chartCardBody: {
      flex: 1,
      minHeight: 0,
      borderRadius: "8px",
    },
    chartTitle: {
      color: "#E0E0E0",
      fontSize: "13px",
      fontWeight: 700,
      marginBottom: "8px",
      lineHeight: "18px",
    },
    insightsContainer: {
      flex: 1,
      overflow: "auto",
      minHeight: 0,
    },
    tabBar: {
      display: "flex",
      gap: "8px",
      marginTop: "12px",
      flexWrap: "wrap",
    },
    tabButton: {
      border: "1px solid rgba(255,255,255,0.14)",
      borderRadius: "999px",
      padding: "8px 16px",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      cursor: "pointer",
      fontWeight: 700,
      transition: "all 0.15s ease",
      "&:hover": {
        background: "rgba(255,255,255,0.12)",
      },
    },
    tabButtonActive: {
      background: theme.globallink.colors.Blue[500],
      borderColor: theme.globallink.colors.Blue[500],
      color: "#FFFFFF",
    },
    title: {
      color: "#E0E0E0",
      fontSize: "16px",
      fontWeight: "bolder",
      display: "flex",
      justifyContent: "space-between",
      padding: "4px",
      alignItems: "center",
      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    },
    popoverContainer: {
      display: "flex",
      justifyContent: "end",
      width: "100%",
    },
    topActions: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    resetButton: {
      border: "1px solid rgba(255,255,255,0.16)",
      borderRadius: "8px",
      padding: "6px 10px",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: "12px",
      transition: "all 0.15s ease",
      "&:hover": {
        background: "rgba(255,255,255,0.12)",
      },
    },
  });

  const classes = useStyles();
  const tradeAnalysisSpotWindow = getTradeAnalysisSpotWindow(executionTimestamp);

  useEffect(() => {
    if (!useContext) return;

    try {
      if (!context) {
        return;
      }

      if (isBestXSelectedTradeContext(context)) {
        const nextLandingPrefill = buildTradeAnalysisLandingPrefill(context);
        const missingFields = getTradeAnalysisMissingFields(nextLandingPrefill);

        setLandingPrefill(nextLandingPrefill);
        setCurrencyPair(nextLandingPrefill.currencyPair);
        setTradeType(
          nextLandingPrefill.tradeType || "BUY"
        );
        setExecutionTimestamp(nextLandingPrefill.executionTimestamp);
        setExecutionSpotRate(nextLandingPrefill.executionSpotRate);
        setExecutionForwardPoints(nextLandingPrefill.executionForwardPoints);
        setExpiration_timestamp(nextLandingPrefill.expirationTimestamp);
        setActiveTab("summary");

        if (missingFields.length > 0) {
          setLandingRequiredFields(missingFields);
          setLandingNotice(
            "We received partial BestX trade details. Please review the landing page, fill in the missing fields, and continue."
          );
          setHasSubmitted(false);
          return;
        }

        setLandingRequiredFields([]);
        setLandingNotice("");
        setHasSubmitted(true);
        return;
      }

      const currencyPairReceived = context.instruments?.[0]?.id?.ticker;
      if (currencyPairReceived && currencyList.includes(currencyPairReceived)) {
        setCurrencyPair(currencyPairReceived);
      }
    } catch {
      console.log("Some Error Happened");
    }
  }, [context, currencyList, useContext]);

  useEffect(() => {
    setSessionVariable(currencyPair);
  }, [currencyPair]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 580px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setUseCompactCharts(event.matches);
    };

    setUseCompactCharts(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (!hasSubmitted || !currencyPair || !executionTimestamp) return;

    summaryCacheRef.current = "";
    summaryVersionRef.current = 0;

    const cleanup = handleFetchSummary({
      currencyPair,
      tradeType,
      executionTimestamp,
      executionSpotRate,
      expiration_timestamp,
      executionForwardPoints,
      setSummary,
      summaryCacheRef,
      summaryVersionRef,
      setLoading,
    });

    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [
    hasSubmitted,
    expiration_timestamp,
    executionForwardPoints,
    executionSpotRate,
    executionTimestamp,
    currencyPair,
    tradeType,
  ]);

  if (!hasSubmitted) {
    return (
      <TradeAnalysisLandingPage
        currencyList={currencyList}
        onSubmit={handleTradeSubmit}
        initialTrade={landingPrefill ?? undefined}
        notice={landingNotice}
        requiredFields={landingRequiredFields}
      />
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        <TradeAnalysisTradeHeader
          currencyPair={currencyPair}
          tradeType={tradeType}
          executionTimestampUtc={executionTimestamp}
          executionSpotRate={executionSpotRate}
          expiryDateUtc={expiration_timestamp}
          executionForwardPoints={executionForwardPoints}
        />

        <div className={classes.topActions}>
          <button
            type="button"
            className={classes.resetButton}
            onClick={handleResetToLanding}
          >
            Reset
          </button>
          <TradeAnalysisPopOverMenu
            setCurrencyPair={setCurrencyPair}
            setUseContext={setUseContext}
            useContext={useContext}
            currencyPair={currencyPair}
            currencyList={currencyList}
            tenorList={tenorList}
            setTenor={setTenors}
            setUserLanguage={setUserLanguage}
            userLanguage={userLanguage}
            setExecutionTimestamp={setExecutionTimestamp}
            setExecutionSpotRate={setExecutionSpotRate}
            setExpirationDate={setExpiration_timestamp}
            setExecutionForwardPoints={setExecutionForwardPoints}
            tradeType={tradeType}
            setTradeType={setTradeType}
            executionTimestamp={executionTimestamp}
            executionSpotRate={executionSpotRate}
            executionForwardPoints={executionForwardPoints}
            expirationTimestamp={expiration_timestamp}
          />
        </div>
      </div>
      <div className={classes.chartSection}>
        {useCompactCharts ? (
          <>
            <div className={classes.tabBar}>
              <button
                type="button"
                className={`${classes.tabButton} ${
                  activeChartTab === "prices" ? classes.tabButtonActive : ""
                }`}
                onClick={() => setActiveChartTab("prices")}
              >
                Spot Prices
              </button>
              <button
                type="button"
                className={`${classes.tabButton} ${
                  activeChartTab === "forward" ? classes.tabButtonActive : ""
                }`}
                onClick={() => setActiveChartTab("forward")}
              >
                Forward Points
              </button>
            </div>
            <div className={classes.chartCard}>
              <div className={classes.chartTitle}>
                {activeChartTab === "prices" ? "Spot Prices" : "Forward Points"}
              </div>
              <div className={classes.chartCardBody}>
                {activeChartTab === "prices" ? (
                  <PriceChartTileComponent
                    currencyPair={currencyPair}
                    setCurrentPrice={setCurrentPrice}
                    defaultInterval="1m"
                    customStartDate={tradeAnalysisSpotWindow.startDate}
                    customEndDate={tradeAnalysisSpotWindow.endDate}
                    enableLiveUpdates={false}
                    hideIntervalControl
                    hidePeriodControl
                  />
                ) : (
                  <CoTraderForwardChart
                    currencyPair={currencyPair}
                    setTenorList={handleSetTenorList}
                    tenors={tenors}
                    executionTimestamp={executionTimestamp}
                    chartHeight="200px"
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <div className={classes.chartRow}>
            <div className={classes.chartCard}>
              <div className={classes.chartTitle}>Spot Prices</div>
              <div className={classes.chartCardBody}>
                <PriceChartTileComponent
                  currencyPair={currencyPair}
                  setCurrentPrice={setCurrentPrice}
                  defaultInterval="1m"
                  customStartDate={tradeAnalysisSpotWindow.startDate}
                  customEndDate={tradeAnalysisSpotWindow.endDate}
                  enableLiveUpdates={false}
                  hideIntervalControl
                  hidePeriodControl
                />
              </div>
            </div>
            <div className={classes.chartCard}>
              <div className={classes.chartTitle}>Forward Points</div>
              <div className={classes.chartCardBody}>
                <CoTraderForwardChart
                  currencyPair={currencyPair}
                  setTenorList={handleSetTenorList}
                  tenors={tenors}
                  executionTimestamp={executionTimestamp}
                  chartHeight="200px"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={classes.tabBar}>
        <button
          type="button"
          className={`${classes.tabButton} ${
            activeTab === "summary" ? classes.tabButtonActive : ""
          }`}
          onClick={() => setActiveTab("summary")}
        >
          Summary
        </button>
        <button
          type="button"
          className={`${classes.tabButton} ${
            activeTab === "chat" ? classes.tabButtonActive : ""
          }`}
          onClick={() => setActiveTab("chat")}
        >
          Chat
        </button>
      </div>
      <div className={classes.insightsContainer}>
        {activeTab === "summary" ? (
          <BestxSummaryTileComponent summary={summary} loading={loading} />
        ) : (
          <TradeAnalysisConversationTileComponent
            currencyPair={currencyPair}
            tradeType={tradeType}
            executionTimestamp={executionTimestamp}
            executionSpotRate={executionSpotRate}
            executionForwardPoints={executionForwardPoints}
            expirationTimestamp={expiration_timestamp}
            userLanguage={userLanguage}
          />
        )}
      </div>
    </div>
  );
};

export default TradeAnalysisTile;

const handleFetchSummary = ({
  reqId,
  activeReqRef,
  setSummary,
  summaryCacheRef,
  summaryVersionRef,
  currencyPair,
  tradeType,
  executionTimestamp,
  executionSpotRate,
  expiration_timestamp,
  setLoading,
  executionForwardPoints,
}: any) => {
  setLoading(true);
  setSummary("");

  const apiUrl = process.env.REACT_APP_API_URL;
  const user_id: any = localStorage.getItem("userId");
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Calcutta";
  const userLanguage = localStorage.getItem("user_language") || "English";
  const random_number = Math.floor(100000 + Math.random() * 900000);
  const chat_id = `BESTXGPT-${user_id}-${random_number}`;

  const requestBody = {
    chat_id,
    origin: "BESTX",
    trade_type: tradeType ?? null,
    currency_pair: currencyPair ?? null,
    execution_timestamp: executionTimestamp ?? null,
    execution_spot_rate: executionSpotRate ?? null,
    execution_forward_points: executionForwardPoints ?? null,
    expiration_timestamp,
    embedding_model: "text-embedding-ada-002",
    userTimeZone,
    user_language: userLanguage,
  };

  let isCancelled = false;
  let ws: WebSocket | null = null;

  const openSocket = async () => {
    try {
      const wsUrl = await getAuthenticatedWebSocketUrl(chat_id, user_id);
      if (isCancelled) return;

      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        ws?.send(JSON.stringify(requestBody));
      };

      ws.onmessage = (event) => {
        let data: any;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        const t = data?.type;
        const msg = data?.message;

        if (
          t === "FINISH" ||
          t === "DONE" ||
          t === "END" ||
          t === "COMPLETE"
        ) {
          setLoading(false);
          try {
            ws?.close();
          } catch {}
          return;
        }
        if (t === "SUMMARY_UPDATE") {
          const summaryPayload = parseSummaryPayload(msg);
          summaryCacheRef.current = summaryPayload?.summary || "";
          summaryVersionRef.current = summaryPayload?.version || 0;
          return;
        }
        if (t === "INFO" || t === "ERROR" || t === "MESSAGE_ID") {
          return;
        }

        if (msg != null) {
          setSummary((prev: string) => prev + String(msg));
        }
      };

      ws.onerror = (e) => {
        console.error("WS error", e);
        setLoading(false);
      };
    } catch (error) {
      if (!isCancelled) {
        console.error("WS auth failed", error);
        setLoading(false);
      }
    }
  };

  void openSocket();

  return () => {
    isCancelled = true;
    try {
      ws?.close();
    } catch {}
  };
};
